import { z } from "zod";
import { backdropSchema } from "./backdrop";
import { readingTimeSchema, slideTypeSchema, type SlideType } from "./deck";
import { DEFAULT_LOBBY, lobbySchema } from "./lobby";
import { mediaUrlSchema, takesMedia } from "./media";
import { themeSchema } from "./theme";

/**
 * Presenter → server: authoring decks.
 *
 * Distinct from `deck.ts`, which describes the FROZEN deck a session runs from. These
 * shapes describe the live, editable deck — the thing the snapshot is taken of — and
 * they are allowed to change without touching anything a running session reads.
 */

/** Slide types whose answers are option ids, and which therefore need options. */
export const CHOICE_TYPES = ["mcq", "true_false", "poll"] as const satisfies readonly SlideType[];

/** Slide types that can award points. Everything else is audience voice. */
export const SCORABLE_TYPES = ["mcq", "true_false", "type_answer"] as const satisfies readonly SlideType[];

/** Choice types on which a participant may be allowed to pick more than one option. */
export const MULTI_SELECT_TYPES = ["mcq", "poll"] as const satisfies readonly SlideType[];

const isChoice = (t: SlideType) => (CHOICE_TYPES as readonly string[]).includes(t);
const isScorable = (t: SlideType) => (SCORABLE_TYPES as readonly string[]).includes(t);

/**
 * How much text a slide holds, in characters: as much as the stage and a phone show in
 * full, measured at these lengths (a 1920×1080 projector, phones 360 and 390 wide). A
 * question is read against the clock, so it is held shorter than a content slide, which
 * is the whole slide and is read at leisure. An option is read on a tile a quarter of
 * the screen wide.
 */
export const TEXT_LIMITS = { question: 250, content: 500, option: 100 } as const;

/** The longest a slide's prompt may be: a content slide's text, or anything else's question. */
export function promptLimit(type: SlideType): number {
  return type === "content" ? TEXT_LIMITS.content : TEXT_LIMITS.question;
}

const optionInputSchema = z
  .object({
    label: z.string().trim().min(1).max(TEXT_LIMITS.option),
    isCorrect: z.boolean().default(false),
  })
  .strict();

/**
 * The per-slide settings the live path reads, and nothing else.
 *
 * Strict on purpose. `slides.config` is an open jsonb column and `readSlideConfig`
 * tolerates anything in it, which is right for reading and wrong for writing: a key
 * the server does not act on would be stored, shown back in the editor, and silently
 * ignored on stage.
 */
const slideConfigInputSchema = z
  .object({
    timeLimitMs: z.number().int().min(0).max(3_600_000),
    basePoints: z.number().int().min(0).max(100_000),
    multiplier: z.number().positive().max(10),
    scaleMin: z.number().int().min(-1000).max(1000),
    scaleMax: z.number().int().min(-1000).max(1000),
    maxWords: z.number().int().min(1).max(10),
    /** mcq and poll (MULTI_SELECT_TYPES): participants may pick more than one option. */
    multiSelect: z.boolean(),
    /** type_answer: what counts as right. Up to 200 characters, as a participant can type. */
    acceptedAnswers: z.array(z.string().trim().min(1).max(200)).max(10),
    /** A picture behind this slide alone, in place of the theme's (backdrop.ts). */
    background: backdropSchema.nullable(),
    /** A scored question's moment to read before its answers open (deck.ts, readingDelayMs). */
    readingTime: readingTimeSchema,
  })
  .partial()
  .strict();

/**
 * What is wrong with a slide, as codes rather than sentences: the API turns them into
 * its error messages, and the deck editor into the presenter's own language — one set
 * of rules for both, so the editor never lets through a slide the API will refuse.
 *
 * Each of these is a deck that saves cleanly and then fails in front of a room: a poll
 * nobody can answer, a scored question with no right answer (every response scores
 * zero), options on a scale slide the participant never sees, or a question with two
 * right answers where a phone can pick only one.
 */
export type SlideProblem =
  | { code: "optionCount"; min: number; max: number }
  | { code: "optionsNotAllowed" }
  | { code: "emptyOption"; index: number }
  | { code: "promptTooLong"; max: number }
  | { code: "optionTooLong"; index: number; max: number }
  | { code: "notScorable" }
  | { code: "noCorrectOption" }
  | { code: "multipleCorrect" }
  | { code: "noAcceptedAnswer" }
  | { code: "mediaNotAllowed" };

export function slideProblems(slide: {
  type: SlideType;
  prompt?: string;
  isScored: boolean;
  options: ReadonlyArray<{ label: string; isCorrect: boolean }>;
  mediaUrl?: string | null;
  config?: { multiSelect?: boolean; acceptedAnswers?: readonly string[] };
}): SlideProblem[] {
  const problems: SlideProblem[] = [];
  // Decks written before the limits came down can be longer: the editor says which part
  // to shorten, rather than refusing the save with no reason.
  const promptMax = promptLimit(slide.type);
  if ((slide.prompt ?? "").trim().length > promptMax) problems.push({ code: "promptTooLong", max: promptMax });
  if (isChoice(slide.type)) {
    const [min, max] = slide.type === "true_false" ? [2, 2] : [2, 10];
    if (slide.options.length < min || slide.options.length > max) problems.push({ code: "optionCount", min, max });
    slide.options.forEach((o, index) => {
      if (o.label.trim() === "") problems.push({ code: "emptyOption", index });
      else if (o.label.trim().length > TEXT_LIMITS.option) problems.push({ code: "optionTooLong", index, max: TEXT_LIMITS.option });
    });
  } else if (slide.options.length > 0) {
    problems.push({ code: "optionsNotAllowed" });
  }
  if (slide.isScored && !isScorable(slide.type)) problems.push({ code: "notScorable" });
  if (slide.isScored && isChoice(slide.type)) {
    const correct = slide.options.filter((o) => o.isCorrect).length;
    if (correct === 0) problems.push({ code: "noCorrectOption" });
    // A right answer is ALL the correct options. With one pick per phone, nobody could
    // ever give it.
    if (correct > 1 && slide.type === "mcq" && slide.config?.multiSelect !== true) {
      problems.push({ code: "multipleCorrect" });
    }
  }
  if (slide.isScored && slide.type === "type_answer") {
    if (!(slide.config?.acceptedAnswers ?? []).some((a) => a.trim() !== "")) problems.push({ code: "noAcceptedAnswer" });
  }
  if (slide.mediaUrl && !takesMedia(slide.type)) problems.push({ code: "mediaNotAllowed" });
  return problems;
}

function problemMessage(type: SlideType, p: SlideProblem): { path: string; message: string } {
  switch (p.code) {
    case "optionCount":
      return {
        path: "options",
        message:
          p.min === p.max
            ? `${type} needs exactly ${p.min} options`
            : `${type} needs between ${p.min} and ${p.max} options`,
      };
    case "optionsNotAllowed":
      return { path: "options", message: `${type} slides take no options` };
    case "emptyOption":
      return { path: "options", message: `Option ${p.index + 1} has no label` };
    case "promptTooLong":
      return { path: "prompt", message: `Slide text is at most ${p.max} characters on ${type} slides` };
    case "optionTooLong":
      return { path: "options", message: `Option ${p.index + 1} is longer than ${p.max} characters` };
    case "notScorable":
      return { path: "isScored", message: `${type} slides cannot be scored` };
    case "noCorrectOption":
      return { path: "options", message: "A scored slide needs at least one correct option" };
    case "multipleCorrect":
      return {
        path: "config.multiSelect",
        message: "More than one correct option needs multiSelect, or participants can pick only one",
      };
    case "noAcceptedAnswer":
      return { path: "config.acceptedAnswers", message: "A scored type_answer slide needs at least one accepted answer" };
    case "mediaNotAllowed":
      return { path: "mediaUrl", message: `${type} slides take no image` };
  }
}

const slideInputSchema = z
  .object({
    type: slideTypeSchema,
    // The longest any slide takes; a question's own, shorter limit is a slide problem.
    prompt: z.string().trim().max(TEXT_LIMITS.content).default(""),
    isScored: z.boolean().default(false),
    config: slideConfigInputSchema.default({}),
    options: z.array(optionInputSchema).max(10).default([]),
    /** An image uploaded to this organisation (media.ts), on the types with room for one. */
    mediaUrl: mediaUrlSchema.nullable().default(null),
  })
  .strict()
  .superRefine((slide, ctx) => {
    for (const problem of slideProblems(slide)) {
      // Empty and over-long labels are already refused, with their own paths, by optionInputSchema.
      if (problem.code === "emptyOption" || problem.code === "optionTooLong") continue;
      const { path, message } = problemMessage(slide.type, problem);
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: path.split("."), message });
    }
  });

export type SlideInput = z.input<typeof slideInputSchema>;

/** What a deck holds: its title, slides and look. Where it is filed is not part of it. */
const deckContentSchema = z.object({
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().max(1000).nullable().default(null),
  slides: z.array(slideInputSchema).max(200).default([]),
  /** Absent: the deck has no look of its own, and the organisation's branding applies. */
  theme: themeSchema.nullable().default(null),
  lobby: lobbySchema.default(DEFAULT_LOBBY),
});

export const createDeckSchema = deckContentSchema
  .extend({
    /** The folder to create it in; null for the top level. */
    folderId: z.string().uuid().nullable().default(null),
  })
  .strict();

export type CreateDeckRequest = z.infer<typeof createDeckSchema>;
/** What a caller may send: every defaulted field is optional. */
export type CreateDeckInput = z.input<typeof createDeckSchema>;

/**
 * Replacing a deck's contents: the whole deck, as the editor holds it. Slides are
 * rewritten rather than patched — nothing refers to a deck's slide ids (a session runs
 * from its own frozen copy), and a whole-deck write cannot leave the deck half-edited.
 *
 * Without a folder: moving a deck is its own request (`moveDeckSchema`), so an editor
 * saving the deck can never move it back to where it was when the editor opened.
 */
export const updateDeckSchema = deckContentSchema.strict();
export type UpdateDeckRequest = z.infer<typeof updateDeckSchema>;

/** A deck as the editor opens it: every setting resolved, ready to edit and send back. */
export const deckDetailSchema = z
  .object({
    deckId: z.string().uuid(),
    title: z.string(),
    description: z.string().nullable(),
    updatedAt: z.string().datetime(),
    /** The folder it is filed in, for the editor's way back; null at the top level. */
    folderId: z.string().uuid().nullable(),
    /** Null when the deck has none of its own. */
    theme: themeSchema.nullable(),
    lobby: lobbySchema,
    slides: z.array(
      z
        .object({
          type: slideTypeSchema,
          prompt: z.string(),
          isScored: z.boolean(),
          config: slideConfigInputSchema.required(),
          options: z.array(z.object({ label: z.string(), isCorrect: z.boolean() }).strict()),
          mediaUrl: z.string().nullable(),
        })
        .strict(),
    ),
  })
  .strict();

export type DeckDetail = z.infer<typeof deckDetailSchema>;
export type DeckSlide = DeckDetail["slides"][number];

/** One row of the dashboard's deck list. */
export const deckSummarySchema = z
  .object({
    deckId: z.string().uuid(),
    title: z.string(),
    description: z.string().nullable(),
    slideCount: z.number().int().nonnegative(),
    /** Each slide's type, in order — enough to draw the deck at a glance. */
    slideTypes: z.array(slideTypeSchema),
    /** The deck's colours, for its card — without the logo, which the list does not need to carry. */
    look: z.object({ background: z.string(), accent: z.string() }).strict().nullable(),
    /** The folder it is filed in; null at the top level. */
    folderId: z.string().uuid().nullable(),
    updatedAt: z.string().datetime(),
  })
  .strict();

export type DeckSummary = z.infer<typeof deckSummarySchema>;

/** One row of the dashboard's session list — enough to find a session and resume it. */
export const sessionSummarySchema = z
  .object({
    sessionId: z.string().uuid(),
    status: z.enum(["lobby", "live", "ended"]),
    mode: z.enum(["live", "self_paced"]).default("live"),
    closesAt: z.string().datetime().nullable().default(null),
    joinCode: z.string(),
    /** From the snapshot, so it survives the source deck being renamed or deleted. */
    deckTitle: z.string(),
    participantCount: z.number().int().nonnegative(),
    createdAt: z.string().datetime(),
  })
  .strict();

export type SessionSummary = z.infer<typeof sessionSummarySchema>;
